import { useState } from 'react';
import type { PuzzleDefinition } from '../domain/puzzle/PuzzleDefinition';
import { themeLabel } from '../domain/puzzle/catalog';
import { puzzleThemes } from './puzzleThemes';

export function PuzzleFacts({
  puzzle,
  compact = false,
  showThemes = true,
  knownTheme,
}: {
  puzzle: PuzzleDefinition;
  compact?: boolean;
  /** False while solving: motif names give the answer away. */
  showThemes?: boolean;
  /** Theme practice already names its motif, so it is never a spoiler. */
  knownTheme?: string;
}) {
  const all = puzzleThemes(puzzle.themes);
  const themes = showThemes ? all : all.filter((theme) => theme === knownTheme);
  const [copyStatus, setCopyStatus] = useState('');
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(puzzle.id);
      setCopyStatus('Puzzle ID copied.');
    } catch {
      setCopyStatus('Could not copy. Select the puzzle ID to copy it.');
    }
  };
  return (
    <dl className={`puzzle-facts ${compact ? 'compact-facts' : ''}`} aria-label="Puzzle facts">
      <div>
        <dt>Rating</dt>
        <dd>{puzzle.rating}</dd>
      </div>
      <div className="puzzle-identity">
        <dt>Puzzle</dt>
        <dd>
          <span data-testid="puzzle-id" title={`#${puzzle.id}`}>
            #{puzzle.id}
          </span>
          <button aria-label="Copy puzzle ID" onClick={() => void copyId()}>
            <svg
              viewBox="0 0 24 24"
              className="design-icon"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="8" y="8" width="12" height="13" rx="2" />
              <path d="M15 8V3H3v12h5" />
            </svg>
          </button>
          <span className="sr-only" role="status">
            {copyStatus}
          </span>
        </dd>
      </div>
      <div className="puzzle-themes">
        <dt>Themes</dt>
        <dd tabIndex={0}>
          {themes.map((theme) => (
            <span key={theme}>{themeLabel(theme)}</span>
          ))}
          {!showThemes && all.length > themes.length ? (
            <em className="themes-hidden" data-testid="themes-hidden">
              {themes.length ? 'More after solving' : 'Themes after solving'}
            </em>
          ) : (
            !themes.length && '—'
          )}
        </dd>
      </div>
    </dl>
  );
}
